import { useSession } from "next-auth/react";
import Link from "next/link";
import React, { Suspense, useState } from "react";
import RequestFundingButton from "~/components/reimbursement-components/RequestFundingButton";
import useAttendee from "~/hooks/useAttendee";

import useIsRegistered from "~/hooks/useIsRegistered";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import { hasEndedUX } from "~/utils/event";
import { If } from "../../Conditional";
import { Button } from "../../themed/Button";
import { checkIfUserIsHost } from "../EventRsvpButton";
import { hiddenCommunitySlugs } from "~/utils/constants";
import { GoInfo } from "react-icons/go";
import { FollowButton } from "../../follow/FollowButton";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";
import { useWhiteLabel } from "~/app/providers";

type Props = {
    event: EventBySlugOutput;
    refetch: () => Promise<void> | void;
    children?: React.ReactNode;
    rSVPButtonControl?: React.ReactNode;
    isFollowing?: boolean;
};

const EventRSVPControls = ({
    event,
    children,
    refetch: externalRefetch,
    rSVPButtonControl,
    isFollowing: isFollowingProp,
}: Props) => {
    const { data: session } = useSession();
    const isUserHost = checkIfUserIsHost(event, session?.user);
    const customization = useWhiteLabel()?.customization;
    const whiteProps = useWhiteLabel()?.whiteLabelProps;
    const feedbackUrl = whiteProps?.slug
        ? `/beta/${whiteProps?.slug}/events/${event.slug}/feedback`
        : `/beta/events/${event.slug}/feedback`;

    const { isRegistered } = useIsRegistered(event);
    const { attendee, attendeeRefetch } = useAttendee(event, isRegistered);
    const [isFollowing, setIsFollowing] = useState(isFollowingProp);

    const refetch = async () => {
        attendeeRefetch();
        await externalRefetch();
    };

    return (
        <div className="flex w-full flex-col items-center">
            <div className="w-full space-y-2">
                <If condition={!hasEndedUX(event)}>
                    <div className="flex flex-wrap justify-center gap-2 md:justify-end">
                        {children}
                        <Suspense
                            fallback={
                                <div className="flex w-full animate-pulse cursor-not-allowed items-center justify-center rounded-full bg-v3-N-500 px-4 py-2 text-center text-white shadow-md">
                                    Loading...
                                </div>
                            }
                        >
                            {rSVPButtonControl}
                        </Suspense>
                    </div>
                    <If
                        condition={
                            !isUserHost &&
                            !hiddenCommunitySlugs.includes(
                                event?.community?.slug ?? "",
                            )
                        }
                    >
                        {event.isApplyToHostAllowed && !event.hosts.length && (
                            <div className="flex w-full items-center">
                                <AboutHostingLink
                                    customization={customization}
                                    hostingLink={whiteProps?.hostingLink}
                                />
                            </div>
                        )}
                        {event.communityId && !isFollowing && (
                            <div className="inline-block w-full text-left">
                                Can't make it?
                                <FollowButton
                                    text="Follow the community"
                                    communityId={event.communityId}
                                    variant="link"
                                    className={cn(
                                        "w-auto grow-0 bg-transparent px-2 underline hover:text-v3-primary disabled:bg-transparent disabled:text-v3-N400",
                                        "shadow-none ring-0",
                                        customization?.linkClassName,
                                    )}
                                    variantFollowing="link"
                                    classNameFollowing="text-v3-N400"
                                    onFollowingUpdate={setIsFollowing}
                                    showSubmitSuffix
                                    initialValue={isFollowing}
                                />
                                for future events.
                            </div>
                        )}
                    </If>
                    <If
                        condition={
                            isUserHost &&
                            !!event.community?.reimbursementConfiguration
                                ?.isReimbursementRequestRequired
                        }
                    >
                        <If condition={!!event.currentUserReimbursementRequest}>
                            <Link
                                href={`/reimbursement-requests/${event.currentUserReimbursementRequest?.slug}`}
                            >
                                <Button
                                    variant="secondary"
                                    className="mt-2 w-full"
                                >
                                    Reimbursement details
                                </Button>
                            </Link>
                        </If>
                        <If condition={!event.currentUserReimbursementRequest}>
                            <RequestFundingButton
                                event={event}
                                refetch={refetch}
                            />
                        </If>
                    </If>
                </If>
                <If condition={hasEndedUX(event)}>
                    <div
                        className={cn(
                            "flex w-full flex-col gap-2 rounded-[10px] p-4",
                            customization?.className,
                            customization?.mainCardClassName || "bg-v3-N-500",
                        )}
                    >
                        <div className="flex w-full flex-col space-y-2">
                            <p
                                className={cn(
                                    "text-base text-v3-N-300",
                                    customization?.labelClassName,
                                )}
                            >
                                This event has ended
                            </p>
                            <div className="flex flex-row gap-2">
                                <If condition={isUserHost}>
                                    <If
                                        condition={
                                            !!event.currentUserReimbursementRequest
                                        }
                                    >
                                        <Link
                                            href={`/reimbursement-requests/${event.currentUserReimbursementRequest?.slug}`}
                                            className="w-full"
                                        >
                                            <Button
                                                variant="secondary"
                                                className="w-full"
                                            >
                                                {event
                                                    .currentUserReimbursementRequest
                                                    ?.status === "APPROVED"
                                                    ? "Claim reimbursement"
                                                    : "Reimbursement details"}
                                            </Button>
                                        </Link>
                                    </If>
                                </If>

                                <If
                                    condition={
                                        (isUserHost ||
                                            attendee?.status === "ATTENDING") ??
                                        false
                                    }
                                >
                                    <Link href={feedbackUrl} className="w-full">
                                        <Button
                                            variant="secondary"
                                            className="w-full"
                                        >
                                            Give feedback
                                        </Button>
                                    </Link>
                                </If>
                            </div>
                        </div>
                    </div>
                </If>
            </div>
        </div>
    );
};

const AboutHostingLink = ({
    customization,
    hostingLink,
}: {
    customization?: CustomizableLayout;
    hostingLink?: string;
}) => (
    <div className="">
        <Link
            className={cn(
                "flex items-center gap-2 text-white hover:underline",
                customization?.linkClassName,
            )}
            href={
                hostingLink ??
                "https://www.getriver.io/articles/the-art-of-hosting"
            }
            target="_blank"
        >
            <GoInfo
                className={cn("text-white", customization?.labelClassName)}
            />{" "}
            What's involved in hosting?
        </Link>
    </div>
);

export default EventRSVPControls;
